import type { DataGridFilterState, DataGridSortingState } from '@ibsys/kit';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { PhraseListParams } from '@/entities/phrases';
import { parseAudioAuditQuery, writeAudioAuditQuery } from './audioAuditQuery';
import { toPhraseListParams } from './toPhraseListParams';
import type { AudioAuditState } from './types';

type Result = AudioAuditState & {
  /** Условия запроса очередной страницы перечня */
  params: PhraseListParams;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSortingChange: (sorting: DataGridSortingState) => void;
  onFiltersChange: (filters: DataGridFilterState) => void;
};

/**
 * Состояние перечня реплик: окно, отбор и порядок вместе с условиями запроса. Живёт в адресе
 * раздела — так возврат из карточки диалога реплики приводит к прежнему перечню.
 * Смена отбора, порядка или размера страницы возвращает перечень к первой странице —
 * прежнее смещение к новому составу отношения не имеет.
 */
export const useAudioAuditState = (): Result => {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo(() => parseAudioAuditQuery(searchParams), [searchParams]);
  const params = useMemo(() => toPhraseListParams(state), [state]);

  const apply = useCallback(
    (patch: Partial<AudioAuditState>) => {
      // Поверх текущей записи истории: иначе каждая смена отбора, порядка и страницы копила бы
      // запись, и «Назад» браузера отматывал бы раздел по одному изменению вместо ухода из него.
      setSearchParams((prev) => writeAudioAuditQuery(prev, { ...parseAudioAuditQuery(prev), ...patch }), {
        replace: true,
      });
    },
    [setSearchParams],
  );

  const onPageChange = useCallback((page: number) => apply({ page }), [apply]);

  const onPageSizeChange = useCallback((pageSize: number) => apply({ pageSize, page: 0 }), [apply]);

  const onSortingChange = useCallback((sorting: DataGridSortingState) => apply({ sorting, page: 0 }), [apply]);

  const onFiltersChange = useCallback((filters: DataGridFilterState) => apply({ filters, page: 0 }), [apply]);

  return { ...state, params, onPageChange, onPageSizeChange, onSortingChange, onFiltersChange };
};
